import { Injectable, inject } from '@angular/core';
import { Action, Selector, State } from '@ngxs/store';
import type { StateContext } from '@ngxs/store';
import { finalize, from, Observable, tap } from 'rxjs';
import type { Card } from '../../interfaces/ICardItem';
import { TileService } from '../../services/tile.service';
import { AddSalData, DeleteSalData, LoadSalData, UpdateSalData } from './sal-data.actions';

export interface SalDataStateModel {
    isLoading: boolean;
    data: Card[];
    loadedLanguage: string | null;
}

@State<SalDataStateModel>({
    name: 'salDataState',
    defaults: {
        isLoading: false,
        data: [],
        loadedLanguage: null
    }
})
@Injectable()
export class SalDataState {
    private readonly tileService = inject(TileService);

    @Selector()
    static isLoading(state: SalDataStateModel): boolean {
        return state.isLoading;
    }

    @Selector()
    static data(state: SalDataStateModel): Card[] {
        return state.data;
    }

    @Selector()
    static loadedLanguage(state: SalDataStateModel): string | null {
        return state.loadedLanguage;
    }

    @Action(LoadSalData)
    public loadSalData(ctx: unknown, action: { payload: { lang: string } }): Observable<Card[]> {
        const stateContext = ctx as StateContext<SalDataStateModel>;
        const { payload } = action;

        stateContext.patchState({ isLoading: true });

        return from(this.tileService.getCardsByType('data', payload.lang)).pipe(
            tap(data => {
                stateContext.patchState({
                    data,
                    loadedLanguage: payload.lang
                });
            }),
            finalize(() => {
                stateContext.patchState({ isLoading: false });
            })
        );
    }

    @Action(AddSalData)
    public addSalData(ctx: unknown, action: { payload: { data: Card } }): void {
        const stateContext = ctx as StateContext<SalDataStateModel>;
        const state = stateContext.getState();
        stateContext.patchState({
            data: [...state.data, action.payload.data]
        });
    }

    @Action(UpdateSalData)
    public updateSalData(ctx: unknown, action: { payload: { data: Card } }): void {
        const stateContext = ctx as StateContext<SalDataStateModel>;
        const state = stateContext.getState();
        stateContext.patchState({
            data: state.data.map(d => (d.id === action.payload.data.id ? action.payload.data : d))
        });
    }

    @Action(DeleteSalData)
    public deleteSalData(ctx: unknown, action: { payload: { id: number } }): void {
        const stateContext = ctx as StateContext<SalDataStateModel>;
        const state = stateContext.getState();
        stateContext.patchState({
            data: state.data.filter(d => d.id !== action.payload.id)
        });
    }
}
